import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Stack, Button, Tooltip } from '@mui/material';

import { Form, Field } from 'src/components/hook-form';
import { schemaHelper } from 'src/components/hook-form/schema-helper';

import { useAuthContext } from 'src/auth/hooks';
import { hasSuperUserOrBetaAccess } from 'src/auth/utils/permissions';

export const CitationSchema = z.object({
  details: z
    .string()
    .min(3, 'Details are required')
    .max(1000, 'Details must be less than 1000 characters'),
  url: schemaHelper.url(),
  body: z
    .string()
    .max(200000, 'Body must be less than 200000 characters')
    .optional(),
});

type CitationSchemaType = z.infer<typeof CitationSchema>;

type CitationFormProps = {
  onCancel: () => void;
  onSubmit: (data: CitationSchemaType) => void;
  mode?: 'create' | 'edit';
  initialValues?: CitationSchemaType;
};

export function CitationForm({
  onCancel,
  onSubmit,
  mode = 'create',
  initialValues,
}: CitationFormProps) {
  const methods = useForm<CitationSchemaType>({
    mode: 'all',
    resolver: zodResolver(CitationSchema),
    defaultValues: initialValues || {
      details: '',
      url: '',
      body: '',
    },
  });

  const { userProfile } = useAuthContext();
  const showBody = hasSuperUserOrBetaAccess(userProfile);

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methods;

  return (
    <Form methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Field.Text name="details" label="Details" multiline rows={3} />
        <Field.Text name="url" label="URL (optional)" />
        {showBody && (
          <Tooltip title="The body is used by Atlas to read the full text of the citation source." placement="top" arrow>
            <span>
              <Field.Text name="body" label="Body (optional)" multiline rows={2} />
            </span>
          </Tooltip>
        )}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={isSubmitting || !isValid}>
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>
    </Form>
  );
}
